import dayjs from "dayjs";
//-- Function to format date for Status Action Date
export const formatDate = (dateString: string | null | undefined) => {
  if (!dateString) return " ";
  const date = new Date(dateString);
  return date.toLocaleString("en-US", {
    timeZone: "UTC",
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "numeric",
    hour12: true,
  });
};

/**
 * Converts UTC date string to dd-mm-yy format
 * @param dateString - ISO date string (e.g., "2026-01-04T23:50:09Z")
 * @returns Formatted date string in dd-mm-yy format (e.g., "04-01-26")
 */
export const formatDateToDDMMYY = (dateString: string): string => {
  if (!dateString) return "";

  const date = new Date(dateString);

  // Check if date is valid
  if (isNaN(date.getTime())) return "";

  const day = String(date.getUTCDate()).padStart(2, "0");
  const month = String(date.getUTCMonth() + 1).padStart(2, "0"); // Months are 0-indexed
  const year = String(date.getUTCFullYear()).slice(-2); // Get last 2 digits of year

  return `${day}-${month}-${year}`;
};

export function formatToHumanReadable(isoString: string): string {
  // Fix invalid ISO: remove trailing "Z" if offset exists
  const cleaned = isoString.replace(/(\+\d{2}:\d{2})Z$/, "$1");

  const date = new Date(cleaned);

  if (isNaN(date.getTime())) {
    throw new Error("Invalid date format: " + isoString);
  }

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = String(date.getFullYear()).slice(-2);

  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, "0");

  const isPM = hours >= 12;
  hours = hours % 12 || 12;

  const period = isPM ? "p.m." : "a.m.";

  return `${day}/${month}/${year}, ${hours}:${minutes} ${period}`;
}

export const formatDateToDDMMYYYY = (dateString: string): string => {
  if (!dateString) return "";
  return dayjs(dateString).format("DD-MM-YYYY");
};

//-- Function to capitalize each word
export function capitalizeEachWords(str: string) {
  return str.replace(/\b\w/g, (char: any) => char.toUpperCase());
}

//-- Function to get initials ex : "John Doe" => "JD"
export const getInitials = (name: string) =>
  name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase();

//-- Function to random two digits number
export function getRandomTwoDigitNumber(): number {
  return Math.floor(Math.random() * 90) + 10;
}

export function formatPercentage(num: number | string | undefined) {
  if (num === undefined) return "";
  const numValue = typeof num === "string" ? parseFloat(num) : num;
  if (isNaN(numValue)) return "";
  const rounded = Math.round(numValue * 100);
  return `${rounded}%`;
}

export const formatPercentageNonRound = (value: unknown): string => {
  const num = Number(value);
  if (isNaN(num)) return "0.00%";

  const percentage = num * 100;

  return `${percentage.toFixed(2)}%`;
};
export const formatPercentageNonRoundUptoTwo = (value: unknown): string => {
  const num = Number(value);
  if (isNaN(num)) return "0.00%";

  const percentage = num * 100;

  return `${percentage.toFixed(2)}%`;
};

//-- Function to get date 7 days ago from current date
export function getSevenDaysAgoDate() {
  const today = new Date();
  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(today.getDate() - 3);
  return sevenDaysAgo.toISOString().split("T")[0];
}

//-- Check for start date before end date ranges
export function isGreaterEndDate(dateRanges: any) {
  const { startDate, endDate } = dateRanges;
  if (startDate && endDate) {
    return dayjs(startDate).isAfter(dayjs(endDate)); // Return true if the start date is after the end date; // Return true if the date range is invalid
  }
}

//-- Function to get filtered summary
export function getFilteredSummary(data = {}) {
  return Object.entries(data)
    .filter(([key]) => key.toLowerCase() !== "total")
    .map(([key, value]: any) => ({
      name: key,
      value,
    }));
}

//-- Function to convert object to chart data
export function convertObjectToChartData(obj: Record<string, number>) {
  return Object.entries(obj).map(([name, value]) => ({
    name,
    value,
  }));
}

export const validateTextField = (value: any, length: number = 3) => {
  if (!value.trim()) {
    return "This field cannot be empty";
  }
  if (value.length < length) {
    return `Must be at least ${length} characters`;
  }
  if (/^[ _]/.test(value)) {
    return "Cannot start with a space or underscore (_).";
  }
  if (/^\d/.test(value)) {
    return "Cannot start with a number.";
  }
  if (/[ _]$/.test(value)) {
    return "Cannot end with a space or underscore (_).";
  }
  if (/[^A-Za-z0-9_ ]/.test(value)) {
    return "Only letters, numbers, single spaces, and underscores (_) are allowed.";
  }
  if (/\s{2,}/.test(value)) {
    return "Double spaces are not allowed.";
  }

  return "";
};

export const validateDescriptionField = (value: string, length: number = 3) => {
  const trimmedValue = value.trim();

  if (!trimmedValue) {
    return "This field is required.";
  }

  if (trimmedValue.length < length) {
    return `Description must be at least ${length} characters.`;
  }

  if (trimmedValue.length > 250) {
    return "Description must not exceed 250 characters.";
  }

  return ""; // No error
};

//-- Function to convert snake case to camel case
export const toCamelCase = (str: string): string =>
  str.replace(/_([a-z])/g, (_, g) => g.toUpperCase());

// Basic URL validation
export const validateUrl = (url: string) => {
  if (!url) return false;
  try {
    new URL(url);
    return true;
  } catch (_) {
    return false;
  }
};

// Read file as base64 (wrapped in Promise)
export const readFileAsBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = (reader.result as string).split(",")[1];
      resolve(base64 || "");
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

export const formatNumber = (num: number) => num?.toLocaleString("en-US"); // 12,345

export const formatCurrency = (value: number) =>
  `${new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Math.round(value))}$`;

export const formatPercent = (num: number) => `${num?.toFixed(2)}%`;

type WebsiteData = {
  [sharedUid: string]: {
    site_name: string;
    site_url: string;
    ui_site_name: string;
    dimensions: {
      dimension_shared_uid: string;
      dimension_name: string;
    }[];
  };
};

export function getSiteInfo(
  websites: WebsiteData[],
  input: string | string[]
): string | string[] | null {
  const isArray = Array.isArray(input);
  const inputs = isArray ? input : [input];
  const results: string[] = [];

  inputs.forEach((item) => {
    let found: string | null = null;

    for (const siteObj of websites) {
      const [sharedUid, siteDetails] = Object.entries(siteObj)[0];

      // If item is sharedUid → return site_name
      if (item === sharedUid) {
        found = siteDetails.site_name;
        break;
      }

      // If item is site_name → return sharedUid
      if (item === siteDetails.site_name) {
        found = sharedUid;
        break;
      }

      // (Optional) If item matches dimension_shared_uid → return site_name
      if (
        siteDetails.dimensions.some((dim) => dim.dimension_shared_uid === item)
      ) {
        found = siteDetails.site_name;
        break;
      }
    }

    if (found) results.push(found);
  });

  return isArray ? results : results[0] || null;
}

export const formatTwoDecimal = (value: any, suffix = "$") => {
  if (value == null || value === "" || isNaN(value)) return `0.00${suffix}`;

  return `${Number(value).toFixed(2)}${suffix}`;
};

export const getPreviousMonthActiveUsers = (data: any[]) => {
  if (!data || data.length === 0) return 0;

  // 1️⃣ Get previous month based on TODAY
  const now = new Date();
  const prevMonthDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);

  const prevYear = prevMonthDate.getFullYear();
  const prevMonth = String(prevMonthDate.getMonth() + 1).padStart(2, "0");
  const prevMonthKey = `${prevYear}-${prevMonth}`; // "2025-12"

  // 2️⃣ Find matching non-RR entry
  const previousMonthData = data.find((entry) => {
    if (!entry.month || entry.month.includes("RR")) return false;

    const cleanMonth = entry.month.slice(0, 7); // YYYY-MM
    return cleanMonth === prevMonthKey;
  });

  return Math.round(previousMonthData?.active_users ?? 0);
};
